import { DialogTitle } from "@headlessui/react";
import RedFlowerImage from "~/images/red-flower.png";
import { useEffect, type ReactNode } from "react";
import { useRevalidator, useSearchParams } from "react-router";
import { useCheckoutSession } from "./CheckoutSession";
import {
  EventRequestResolutionReason,
  EventRequestState,
  type EventFragment,
  type EventTicketApprovalRequestInput,
} from "~/__generated__/graphql/graphql";
import { PrimaryButton } from "~/components/Button";
import { PlanDialog } from "~/components/plans/components/PlanDialog";
import { StepIndicator } from "~/components/plans/components/StepIndicator";
import { Text } from "~/components/Text";
import { useUser } from "~/hooks/use-user";
import { useRootEnvironment, useRootLocale } from "~/root";
import { checkoutContactEmailSchema } from "~/utils/contact-email";
import { getRegionalCopy } from "~/utils/localization";
import { ApprovalProfileStep } from "./ApprovalProfileStep";
import { ApprovalQuestionsStep } from "./ApprovalQuestionsStep";
import { approvalAnswersComplete, approvalRequestForm } from "./approval-form";
import { CheckoutEmailStep } from "./CheckoutEmailStep";
import { CheckoutPaymentStep } from "./CheckoutPaymentStep";
import { CheckoutPhoneAuthentication } from "./CheckoutPhoneAuthentication";
import { CheckoutTicketsStep } from "./CheckoutTicketsStep";
import { isTicketCheckoutAvailable } from "./ticketSalesAvailability";
import { TICKET_VARIATION_PARAM } from "./ticketVariations";


type ApprovalStep =
  | "tickets"
  | "phone"
  | "profile"
  | "questions"
  | "email"
  | "payment"
  | "status";

type Props = {
  plan: EventFragment;
  authenticated: boolean;
  userExists: boolean;
  stripePublishableKey: string | undefined;
  ticketVariationsEnabled: boolean;
};

const approvalSteps = new Set<ApprovalStep>([
  "tickets",
  "phone",
  "profile",
  "questions",
  "email",
  "payment",
  "status",
]);

function isApprovalStep(value: string | null): value is ApprovalStep {
  return value !== null && approvalSteps.has(value as ApprovalStep);
}

function hasTicketApprovalProfile(
  user: ReturnType<typeof useUser>["user"],
): boolean {
  if (!user) return false;
  const name = user.name.trim();
  return (
    name.length > 0 &&
    name.toLowerCase() !== "anonymous user" &&
    /\p{L}/u.test(name) &&
    !!user.phoneNumber?.trim() &&
    user.age >= 18 &&
    user.age <= 100 &&
    user.gender.trim().length > 0 &&
    (user.headline.trim().length > 0 || user.bio.trim().length > 0) &&
    user.profileImageUrl.trim().length > 0
  );
}

function nextDetailsStep(
  authenticated: boolean,
  userExists: boolean,
  hasVerifiedPhone: boolean,
  completeProfile: boolean,
): ApprovalStep {
  if (!authenticated || !userExists || !hasVerifiedPhone) return "phone";
  if (!completeProfile) return "profile";
  return "questions";
}

export function ApprovalCheckoutDialog({
  plan,
  authenticated,
  userExists,
  stripePublishableKey,
  ticketVariationsEnabled,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const checkout = useCheckoutSession();
  const { setStep, close } = checkout;
  const revalidator = useRevalidator();
  const { user } = useUser();
  const ticketOutcomeSMSEnabled = useRootEnvironment()?.TICKET_OUTCOME_SMS_ENABLED;
  const needsContactEmail =
    !ticketOutcomeSMSEnabled &&
    !checkout.draft.walletIntentID &&
    !checkoutContactEmailSchema.safeParse(user?.contactEmail).success;
  const nextPaymentStep = needsContactEmail ? "email" : "payment";
  const requestedStep = searchParams.get("step");
  const step = isApprovalStep(requestedStep) ? requestedStep : null;
  const submittedRequest = plan.myRequest?.submittedAt ? plan.myRequest : null;
  const mayStartAnotherCheckout =
    submittedRequest?.state === EventRequestState.Declined ||
    submittedRequest?.state === EventRequestState.Expired ||
    submittedRequest?.state === EventRequestState.InAcceptedWaitlist;
  const acceptedWaitlistClaim =
    submittedRequest?.state === EventRequestState.InAcceptedWaitlist;
  const detailsStep = nextDetailsStep(
    authenticated,
    userExists,
    !!user?.phoneNumber?.trim(),
    hasTicketApprovalProfile(user),
  );
  const retryingOrClaiming =
    mayStartAnotherCheckout && step !== null && step !== "status";
  const requestedDisplayedStep: ApprovalStep | null =
    step !== null && (plan.isAttending || (submittedRequest && !retryingOrClaiming)) ? "status" : step;

  let displayedStep = requestedDisplayedStep;
  if (
    displayedStep === "phone" ||
    displayedStep === "profile" ||
    displayedStep === "questions"
  ) {
    if (!(displayedStep === "profile" && detailsStep === "questions")) displayedStep = detailsStep;
  }
  if (
    (displayedStep === "payment" || displayedStep === "email") &&
    !acceptedWaitlistClaim &&
    (detailsStep !== "questions" ||
      !approvalAnswersComplete(plan.formInputs, checkout.draft.answers, plan.myRequest))
  ) {
    displayedStep = detailsStep;
  }
  if (displayedStep === "payment" || displayedStep === "email") {
    displayedStep = nextPaymentStep;
  }

  const checkoutAvailable = isTicketCheckoutAvailable(plan);
  const showingRequestStatus = displayedStep === "status" && authenticated && userExists;
  const staleStatus = showingRequestStatus && !submittedRequest && !plan.isAttending;
  const open = displayedStep !== null && checkoutAvailable && !staleStatus;

  useEffect(() => {
    if (staleStatus) close();
  }, [staleStatus, close]);

  useEffect(() => {
    if (!open || !showingRequestStatus) return;
    const timer = setInterval(() => { if (revalidator.state === "idle") void revalidator.revalidate(); }, 5000);
    return () => clearInterval(timer);
  }, [open, showingRequestStatus, revalidator]);

  // Match the instant checkout: once this flow is unavailable, retire stale
  // checkout params so a later focus revalidation can't reopen the dialog.
  useEffect(() => {
    if (step === null || checkoutAvailable) return;
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete("step");
        next.delete("discountCode");
        next.delete(TICKET_VARIATION_PARAM);
        return next;
      },
      { replace: true, preventScrollReset: true },
    );
  }, [checkoutAvailable, setSearchParams, step]);

  const reloadAtStep = (nextStep: ApprovalStep) => { void checkout.refreshUser(nextStep); };

  return (
    <PlanDialog open={open} onClose={close} fullScreenOnMobile={!showingRequestStatus} includeLogo={!showingRequestStatus}>
      {checkout.error && <p role="alert" className="mb-3 font-sans text-sm text-butter-pink">{checkout.error} <button type="button" className="underline" onClick={() => void checkout.flush()}>Try again</button></p>}
      {displayedStep === "tickets" && (
        <CheckoutTicketsStep
          plan={plan}
          ticketVariationsEnabled={ticketVariationsEnabled}
          nextStep={acceptedWaitlistClaim ? nextPaymentStep : detailsStep}
        />
      )}
      {displayedStep === "phone" && (
        <ApprovalDetailsStage onBack={() => setStep("tickets")}>
          <CheckoutPhoneAuthentication
            eventId={plan.id}
            onComplete={() => void checkout.refreshUser("questions", true)}
          />
        </ApprovalDetailsStage>
      )}
      {displayedStep === "profile" && (
        <ApprovalDetailsStage onBack={() => setStep("tickets")}>
          <ApprovalProfileStep
            eventId={plan.id}
            host={plan.hosts.find(host => host.primary) ?? plan.hosts[0]}
            onComplete={() => reloadAtStep("questions")}
          />
        </ApprovalDetailsStage>
      )}
      {displayedStep === "questions" && (
        <ApprovalDetailsStage onBack={() => setStep("tickets")}>
          <ApprovalQuestionsStep
            plan={plan}
            onComplete={() => setStep(nextPaymentStep)}
          />
        </ApprovalDetailsStage>
      )}
      {displayedStep === "email" && (
        <CheckoutEmailStep
          eventId={plan.id}
          requiresApproval={!acceptedWaitlistClaim}
          onBack={() => setStep(acceptedWaitlistClaim ? "tickets" : "questions")}
        />
      )}
      {displayedStep === "payment" && (
        <ApprovalPaymentStep
          plan={plan}
          authenticated={authenticated}
          userExists={userExists}
          stripePublishableKey={stripePublishableKey}
          ticketVariationsEnabled={ticketVariationsEnabled}
        />
      )}
      {displayedStep === "status" && (!authenticated || !userExists) ? (
        <CheckoutPhoneAuthentication
          eventId={plan.id}
          onComplete={() => void checkout.refreshUser("status", true)}
        />
      ) : (
        displayedStep === "status" && (
          <RequestStatusStep
            plan={plan}
            isAttending={plan.isAttending}
            onStartCheckout={() => { checkout.update({ walletIntentID: "", paymentPhase: "overview" }); setStep("tickets"); }}
          />
        )
      )}
    </PlanDialog>
  );
}

function ApprovalDetailsStage({
  children,
  onBack,
}: {
  children: ReactNode;
  onBack: () => void;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0">
        <StepIndicator
          currentStep={2}
          showTicketStep
          stepActions={{ 1: onBack }}
        />
      </div>
      {children}
    </div>
  );
}

function ApprovalPaymentStep(props: Props) {
  const { draft } = useCheckoutSession();
  const eventRequestID =
    props.plan.myRequest?.state === EventRequestState.InAcceptedWaitlist
      ? props.plan.myRequest.id
      : undefined;
  const approvalRequest: EventTicketApprovalRequestInput = eventRequestID
    ? { eventRequestID }
    : {
        form: approvalRequestForm(props.plan.formInputs, draft.answers, props.plan.myRequest),
        ...(draft.message ? { message: draft.message } : {}),
      };

  return <CheckoutPaymentStep {...props} approvalRequest={approvalRequest} />;
}

function RequestStatusStep({
  plan,
  isAttending,
  onStartCheckout,
}: {
  plan: EventFragment;
  isAttending: boolean;
  onStartCheckout: () => void;
}) {
  const regionalCopy = getRegionalCopy(useRootLocale());
  const request = plan.myRequest;
  if (isAttending) {
    return (
      <div className="mt-3 flex flex-col items-center gap-5 text-center sm:mt-5">
        <Text as={DialogTitle} variant="primary" size="sm">
          {"You're going"}
        </Text>
        <Text as="p" size="sm" className="leading-relaxed">
          Your ticket is confirmed. Open Butter to view your plan details and ticket.
        </Text>

        <PrimaryButton to={`/plans/${plan.id}/app`} className="text-base!">
          Open in the Butter app
        </PrimaryButton>
      </div>
    );
  }

  if (!request?.submittedAt) return null;

  const isPending = request.state === EventRequestState.Pending;
  const hostFirstName = plan.hosts.find(host => host.primary)?.user.firstName?.trim() || "The host";
  let title = isPending ? "Place requested" : "Request sent";

  let body = isPending
    ? `${hostFirstName}’s got it and usually replies within a day. We’ll let you know the second they do.`
    : "Your request is with the host.";

  if (request.state === EventRequestState.PendingWaitlist) {
    title = "You're on the waitlist";
    body = request.ticketPurchase
      ? `Your payment ${regionalCopy.authorization} has been released. You're still on the waitlist, and we'll let you know if a ${regionalCopy.place} opens up.`
      : `The host is reviewing your waitlist request. If they approve it, we'll let you know when a ${regionalCopy.place} opens up.`;
  } else if (request.state === EventRequestState.Approving) {
    title = regionalCopy.finalizingPayment;
    body = "The host approved your request. We're securing your ticket now.";
  } else if (request.state === EventRequestState.InAcceptedWaitlist) {
    title = `A ${regionalCopy.place} is available`;
    body = `Choose a current ticket and payment method to claim your ${regionalCopy.place}.`;
  } else if (
    request.state === EventRequestState.Declined ||
    request.state === EventRequestState.Expired
  ) {
    title = "This request wasn't approved";
    body = resolutionMessage(request.resolutionReason, regionalCopy);
  }

  return (
    <div className="mt-5 flex flex-col items-center gap-5 text-center">
      {isPending && (
        <img src={RedFlowerImage} alt="" aria-hidden="true" className="size-15 object-contain" />
      )}
      <div className="w-full">
        <Text as={DialogTitle} variant="primary" size={isPending ? "md" : "sm"}>
          {title}
        </Text>
        <Text as="p" size="sm" className="mt-2 leading-relaxed">
          {body}
        </Text>

      </div>

      {request.ticketPurchaseCode?.code && (
        <div className="rounded-xl bg-butter-cream px-6 py-4">
          <Text as="p" variant="secondary" size="sm">
            Your app code
          </Text>
          <p className="font-mono text-base font-normal tracking-[0.2em] text-butter-green">
            {request.ticketPurchaseCode.code}
          </p>
        </div>
      )}
      {request.state === EventRequestState.InAcceptedWaitlist &&
        request.resolutionReason !== EventRequestResolutionReason.EventFull && (
          <PrimaryButton type="button" className="text-base!" onClick={onStartCheckout}>
            Choose a ticket
          </PrimaryButton>
        )}
      <PrimaryButton to={`/plans/${plan.id}/app`} className="text-base!">
        {isPending ? "Get the app" : "Open in the Butter app"}
      </PrimaryButton>
    </div>
  );
}

function resolutionMessage(
  reason: EventRequestResolutionReason | null | undefined,
  regionalCopy: ReturnType<typeof getRegionalCopy>,
) {
  switch (reason) {
    case EventRequestResolutionReason.PaymentFailed:
    case EventRequestResolutionReason.PaymentCancelled:
      return `Your payment ${regionalCopy.authorization} couldn't be completed. You can start a fresh checkout.`;
    case EventRequestResolutionReason.VariationSoldOut:
      return "That ticket type sold out before approval. You can choose another available ticket.";
    case EventRequestResolutionReason.EventFull:
      return "The plan filled up before approval.";
    case EventRequestResolutionReason.ApprovalDeadline:
      return `The host didn't respond before the decision deadline, so the ${regionalCopy.authorization} was released.`;
    default:
      return `The ${regionalCopy.authorization} was released and you weren't charged.`;
  }
}
